import {
  Component,
  Optional,
  Inject,
  EventEmitter,
  Output,
  Input,
  ViewChild,
  ElementRef, OnInit, ViewEncapsulation
} from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  NG_ASYNC_VALIDATORS,
  NG_VALIDATORS,
  AsyncValidatorFn,
  Validator,
  ValidatorFn,
  FormControl,
  FormGroup,
  ControlValueAccessor
} from '@angular/forms';

import {Observable} from 'rxjs';
import {SvgIconUtils} from '../svg-icon/svg-icon-utils';

const noop = () => {};

@Component({
  selector: 'ld-input-password',
  templateUrl: 'input-password.component.html',
  styleUrls: [ './input-password.style.scss' ],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: InputPasswordComponent,
    multi: true
  }],
  encapsulation: ViewEncapsulation.None
})
export class InputPasswordComponent implements ControlValueAccessor, OnInit {

  @Input()
  placeholder = '';

  @Input()
  label: string;

  @Input() displayName: string;
  @Input() validationForm: FormGroup;
  @Input() formControlName: string;
  @Input() shouldCheckIsDirty = false;
  @Input() arePolicyMessagesVisible = true;
  // use to display plaintext messages instead of using the polocy messages.
  // such as for a "confirm password" input
  @Input() displayPlainTextErrors = false;
  @Input() name: string;
  @Input() qa: string;

  private _onTouchedCallback: () => void = noop;
  private _onChangeCallback: (_: any) => void = noop;
  private _blurEmitter: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
  private _focusEmitter: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
  public _focused = false;

  @Output('focus')
  get onFocus(): Observable<FocusEvent> { return this._focusEmitter.asObservable(); }

  @Output('blur')
  get onBlur(): Observable<FocusEvent> { return this._blurEmitter.asObservable(); }

  passwordType: string = 'password';

  private lockIconPath = SvgIconUtils.lockIconPath;
  private strengthNumber: number;
  private errorsToIgnore: string[] = [];

  @ViewChild('strengthMeter') strengthMeter: ElementRef;

  public get passwordControl(): FormControl {
    return <FormControl> this.validationForm.controls[this.formControlName];
  }

  // TODO: investigate if the "ControlValueAccessor" interface is necessary for this component
  writeValue(value: any) {
  }

  registerOnChange(fn: any) {
    this._onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this._onTouchedCallback = fn;
  }

  constructor(
    @Optional() @Inject(NG_VALIDATORS)
    private validators: Array<Validator | ValidatorFn>,
    @Optional() @Inject(NG_ASYNC_VALIDATORS)
    private asyncValidators: Array<Validator | AsyncValidatorFn>
  ) {
    // initializeAccessor(this);
  }

  ngOnInit() {
    // LDTSULA-2475: fixing the console error by reset the password control and score
    this.strengthNumber = 0;
    this.passwordControl.setValue('');
    this.passwordControl.markAsUntouched();
    this.passwordControl.markAsPristine();

    if (!this.displayPlainTextErrors) {
      this.errorsToIgnore = ['required',
                            'letterOrNumber',
                            'minlength',
                            'specialCharacter'];
    }
  }

  handleBlur(event: FocusEvent): void {
    this._blurEmitter.emit(event);
    this._focused = false;
  }

  // used to check that the control is invalid in any way
  hasErrors() {
    return this.validationForm &&
      this.passwordControl.errors &&
      (this.passwordControl.touched || !this.shouldCheckIsDirty) && !this._focused;
  }

  // used to check for specific errors, useful for setting styles on the password rules bullet points
  hasError(error: string) {
    return this.passwordControl.errors === null ? false :
      this.passwordControl.errors['required'] || this.passwordControl.errors[error];
  }

  changePasswordVisibility(isVisible: boolean) {
    this.passwordType = isVisible ? 'text' : 'password';
  }

  changePasswordMessageVisibility(isVisible: boolean, event: FocusEvent) {
    this._focusEmitter.emit(event);
    this._focused = true;
  }

  get strengthScore(): number {
    let passStringArray: String[] = Array.from(this.passwordControl.value as String);
     this.strengthNumber = 0;

    for (let i = 0; i < passStringArray.length; i++) {
      // Add number points
      let numbersArray = passStringArray[i].match( /\d+/g );
      if (numbersArray) {
        let points = 1;
        let length = Array.from( numbersArray[0] ).length;
        this.strengthNumber += points * length;
      }

      // Add letter points
      let letterArray = passStringArray[i].match( /^[A-Za-z]+$/i );
      if (letterArray) {
        let points = 2;
        let length = Array.from( letterArray[0] ).length;
        this.strengthNumber += points * length;
      }

      // Add non alphanumeric points
      let nonAlphaArray = passStringArray[i].match( /[!@#$%^&*()_\-+=,./<>?;:'"\[\]{}|~]+/gi );
      if (nonAlphaArray) {
        let points = 3;
        let length = Array.from( nonAlphaArray[0] ).length;
        this.strengthNumber += points * length;
      }
    }

    return this.strengthNumber;
  }

  get scoreIsFair(): boolean {
    return this.strengthScore > 0;
  }

  get scoreIsGood(): boolean {
    return this.strengthScore >= 16 && !this.passwordControl.errors;
  }

  get scoreIsStrong(): boolean {
    return this.strengthScore >= 21 && !this.passwordControl.errors;
  }

  get scoreMarkerTranslateX(): string {
    if (this.strengthMeter) {
       let meterWidth = this.strengthMeter.nativeElement.offsetWidth;
      if (this.scoreIsStrong) {
        return 'translateX(' + (((meterWidth / 3) * 3) - ((meterWidth / 3) / 2)) + 'px)';
      } else if (this.scoreIsGood) {
        return 'translateX(' + (((meterWidth / 3) * 2) - ((meterWidth / 3) / 2)) + 'px)';
      } else if (this.scoreIsFair) {
        return 'translateX(' + (((meterWidth / 3) * 1) - ((meterWidth / 3) / 2)) + 'px)';
      } else {
        return 'translateX(5px)';
      }
    } else {
      return 'translateX(5px)';
    }
  }
}
